/**
 * scenes-plugin — the `/scenes` home-automation surface as a studio-sibling
 * `ComposableGenomePlugin`.
 *
 * Mirrors `homePlugin`/`devicePlugin`: one `component` editor (`scenes`) routed at
 * `/scenes`, lowering onto `component`/`route` only (NO new `EntityKind` — a scene
 * list is a studio-local concern, not an engine primitive; gate 4 stays green, the
 * route count derives +1). The editor is `React.lazy` so the whole scenes surface
 * (the store, the apply core, the dispatch, the builder) stays in a split chunk
 * OUT of the app entry (gate 8 — the eager module carries only the route + the
 * `React.lazy` boundary).
 *
 * Route-only (no IDE dock region), so `IDE_LAYOUT_VERSION` is untouched.
 *
 * @module @ubiclouds/v5-studio/home/scenes-plugin
 */

import { lazy } from 'react';
import { definePlugin } from '@ubiclouds/v5-sdk/plugins';
import type { ComposableGenomePlugin } from '@ubiclouds/v5-sdk/plugins';

/** The Scenes-surface plugin id. */
export const SCENES_PLUGIN_ID = 'v5-studio-scenes';

/** The split-chunk Scenes surface (the `/scenes` route editor body). */
export const LazyScenesSurface = lazy(() => import('./ScenesSurface.js'));

/**
 * The Scenes plugin — the `/scenes` route → the lazy `scenes` editor. Composed as a
 * SIBLING in `studioPlugins`; it adds no new `EntityKind` (the surface lowers onto
 * `component`/`route`), so gate 4 stays green.
 */
export const scenesPlugin: ComposableGenomePlugin = definePlugin(SCENES_PLUGIN_ID)
  .name('Scenes')
  .trustTier('core')
  .editor('scenes', LazyScenesSurface)
  .route('/scenes', { routesTo: 'component/scenes', title: 'Scenes' })
  .build();
